import {
  Box,
  Autocomplete,
  InputAdornment,
  AutocompleteChangeDetails,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { SyntheticEvent, Dispatch, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'
import theme from 'lib/theme'
import { SearchField } from 'views/SearchConfig/SearchConfig.styled'

type AutocompleteOption = {
  label: string
  value: string
}
type SearchWithSuggestionsProps = {
  label: string
  id: string
  options: AutocompleteOption[]
  onChange: (query: string) => void
  loading: boolean
  onSelect: (option: AutocompleteChangeDetails<AutocompleteOption>) => void
  error: boolean
  helperText: string
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  query: string | null
  placeholderText: string
  clearSearch: () => void
  selectedValue: { value: string; label: string }
}

const SearchWithSuggestions = ({
  options,
  label,
  id,
  onChange,
  onSelect,
  loading,
  error,
  helperText,
  isOpen,
  setIsOpen,
  query,
  placeholderText,
  clearSearch,
  selectedValue,
}: SearchWithSuggestionsProps) => {
  const [focus, setFocus] = useState(false)
  const [hover, setHover] = useState(false)
  const { t } = useTranslation()

  return (
    <Autocomplete
      disableClearable
      forcePopupIcon={false}
      options={selectedValue ? [selectedValue, ...options] : options}
      id={id}
      open={isOpen}
      role='search'
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      onInputChange={(
        event: SyntheticEvent<Element, Event>,
        value: string,
        reason: string
      ) => {
        if (reason === 'reset') {
          setIsOpen(false)
        }
        if (reason === 'input') {
          if (value.length > 2) {
            onChange(value)
            setIsOpen(true)
          } else {
            setIsOpen(false)
          }
        }
      }}
      onChange={(e, value, reason, details) => {
        if (reason === 'selectOption') {
          if (details) {
            onSelect(details)
          }
        }
      }}
      loading={loading}
      disablePortal
      isOptionEqualToValue={(option, value) => option.label === value.label}
      getOptionLabel={(option) => (option as AutocompleteOption).label ?? ''}
      value={selectedValue}
      renderOption={(props, option) => (
        <Box component='li' {...props} key={option.value}>
          {option.label}
        </Box>
      )}
      noOptionsText={t('noOptionsAutoComplete')}
      renderInput={(params) => (
        <SearchField
          {...params}
          size='small'
          onMouseOver={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          hover={hover}
          paddingLeft='0px'
          error={error}
          helperText={
            (!query || (query && query.length < 3)) && focus ? helperText : ''
          }
          label={label}
          sx={{
            '& .MuiOutlinedInput-notchedOutline legend': {
              display: 'none',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              top: 0,
              height: 'inherit',
            },
            '& label': {
              top: '-5px',
              left: '-10px',
              fontWeight: 'bold',
            },
          }}
          placeholder={placeholderText}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            sx: {
              fontWeight: 'bold',
              height: '43px',
              '&.MuiOutlinedInput-root': {
                padding: '6px',
              },
            },
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onMouseDown={clearSearch}
                  aria-label={t('clearSearch')}
                  sx={{
                    '&:hover': {
                      cursor: 'pointer',
                      background: 'transparent',
                      color: theme.palette.action.hover,
                    },
                  }}>
                  <CloseIcon
                    sx={{
                      '&:hover': {
                        background: 'transparent',
                      },
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  )
}

export default SearchWithSuggestions
